import { useState } from "react";
import { Badge } from "./Badge";
import { ApiRequestError, type AddCustomerResult, type DestinationKind } from "../types";
import type { ApiClient } from "../api";
import { useAddCustomerMutation } from "../data";

interface AddCustomerPanelProps {
  api: ApiClient;
}

const defaultGhlApiBaseUrl = "https://services.leadconnectorhq.com";
const defaultGhlVersionHeader = "2021-07-28";

export function AddCustomerPanel({ api }: AddCustomerPanelProps) {
  const addCustomer = useAddCustomerMutation(api);
  const [tenantId, setTenantId] = useState("");
  const [submitterId, setSubmitterId] = useState("");
  const [submitterSecret, setSubmitterSecret] = useState("");
  const [destinationHandle, setDestinationHandle] = useState("");
  const [destinationKind, setDestinationKind] = useState<DestinationKind>("ghl");
  const [locationId, setLocationId] = useState("");
  const [pit, setPit] = useState("");
  const [scopes, setScopes] = useState("contacts.write");
  const [apiBaseUrl, setApiBaseUrl] = useState(defaultGhlApiBaseUrl);
  const [versionHeader, setVersionHeader] = useState(defaultGhlVersionHeader);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [lastResult, setLastResult] = useState<AddCustomerResult | null>(null);

  const error = addCustomer.error;
  const resultTone = lastResult?.verify.status === "verified" ? "ok" : "bad";

  return (
    <article className="panel wide" aria-labelledby="addCustomerTitle">
      <div className="panel-header">
        <div>
          <h2 id="addCustomerTitle">Add customer</h2>
          <p className="panel-subtitle">Provision a submitter and one verified destination.</p>
        </div>
        <Badge tone={addCustomer.isPending ? "" : lastResult ? resultTone : ""}>
          {addCustomer.isPending
            ? "Saving"
            : lastResult
              ? lastResult.verify.status
              : "Ready"}
        </Badge>
      </div>

      <form
        className="customer-form"
        onSubmit={(event) => {
          event.preventDefault();
          setLastResult(null);
          const common = {
            tenant_id: tenantId.trim(),
            submitter_id: submitterId.trim(),
            submitter_secret: submitterSecret,
            destination_handle: destinationHandle.trim(),
          };
          const request = destinationKind === "ghl"
            ? {
                ...common,
                destination_kind: "ghl" as const,
                ghl: {
                  location_id: locationId.trim(),
                  access_token: pit,
                  scopes: scopes.split(",").map((scope) => scope.trim()).filter(Boolean),
                  api_base_url: apiBaseUrl.trim(),
                  version_header: versionHeader.trim(),
                },
              }
            : {
                ...common,
                destination_kind: "webhook" as const,
                webhook: {
                  url: webhookUrl.trim(),
                  secret: webhookSecret,
                },
              };
          addCustomer.mutate(request, {
            onSuccess: (result) => {
              setLastResult(result);
              setSubmitterSecret("");
              setPit("");
              setWebhookSecret("");
            },
          });
        }}
      >
        <div className="form-grid">
          <label className="field">
            <span>Tenant ID</span>
            <input
              required
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
              placeholder="tenant-demo"
            />
          </label>
          <label className="field">
            <span>Submitter ID</span>
            <input
              required
              value={submitterId}
              onChange={(event) => setSubmitterId(event.target.value)}
              placeholder="inf_customer"
            />
          </label>
          <label className="field">
            <span>Destination target</span>
            <input
              required
              value={destinationHandle}
              onChange={(event) => setDestinationHandle(event.target.value)}
              placeholder="customer.example"
            />
          </label>
          <label className="field secret-field">
            <span>Submitter secret</span>
            <div className="input-action">
              <input
                required
                type="password"
                value={submitterSecret}
                onChange={(event) => setSubmitterSecret(event.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="ghost"
                onClick={() => setSubmitterSecret(generateSecret())}
              >
                Generate
              </button>
            </div>
          </label>
        </div>

        <div className="segmented-control" role="radiogroup" aria-label="Destination kind">
          <label>
            <input
              type="radio"
              name="destination-kind"
              checked={destinationKind === "ghl"}
              onChange={() => setDestinationKind("ghl")}
            />
            <span>GHL</span>
          </label>
          <label>
            <input
              type="radio"
              name="destination-kind"
              checked={destinationKind === "webhook"}
              onChange={() => setDestinationKind("webhook")}
            />
            <span>Webhook</span>
          </label>
        </div>

        {destinationKind === "ghl" ? (
          <div className="form-grid destination-fields">
            <label className="field">
              <span>Location ID</span>
              <input required value={locationId} onChange={(event) => setLocationId(event.target.value)} />
            </label>
            <label className="field">
              <span>PIT</span>
              <input
                required
                type="password"
                value={pit}
                onChange={(event) => setPit(event.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label className="field">
              <span>Scopes</span>
              <input required value={scopes} onChange={(event) => setScopes(event.target.value)} />
            </label>
            <label className="field">
              <span>API base URL</span>
              <input required value={apiBaseUrl} onChange={(event) => setApiBaseUrl(event.target.value)} />
            </label>
            <label className="field">
              <span>Version</span>
              <input required value={versionHeader} onChange={(event) => setVersionHeader(event.target.value)} />
            </label>
          </div>
        ) : (
          <div className="form-grid destination-fields">
            <label className="field">
              <span>Webhook URL</span>
              <input
                required
                type="url"
                value={webhookUrl}
                onChange={(event) => setWebhookUrl(event.target.value)}
                placeholder="https://customer.example/leads"
              />
            </label>
            <label className="field">
              <span>Webhook secret</span>
              <input
                required
                type="password"
                value={webhookSecret}
                onChange={(event) => setWebhookSecret(event.target.value)}
                autoComplete="new-password"
              />
            </label>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" disabled={addCustomer.isPending}>
            {addCustomer.isPending ? "Provisioning" : "Add customer"}
          </button>
        </div>
      </form>

      {lastResult ? <VerifyResult result={lastResult} /> : null}
      {error ? (
        <p className="panel-error">
          {error instanceof ApiRequestError
            ? `${error.endpoint} failed: ${error.message}`
            : `Request failed: ${(error as Error).message ?? "unknown error"}`}
        </p>
      ) : null}
    </article>
  );
}

function VerifyResult({ result }: { result: AddCustomerResult }) {
  const ok = result.verify.status === "verified";
  return (
    <div className={`verify-result ${ok ? "verify-ok" : "verify-bad"}`} role="status">
      <strong>{ok ? "Verified" : "Unverified"}</strong>
      <span>
        {ok
          ? `${result.customer.destination_handle} is ready for ${result.customer.tenant_id}.`
          : result.verify.reason ?? "Verification failed."}
      </span>
    </div>
  );
}

function generateSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
